/**
 * GelatoMaps — Bootstrap
 * Initializes store, shim, and mounts features in order.
 * @module bootstrap
 */

import { store } from './shared/store.js'
import { hydrateFromLegacy } from './shared/legacy-shim.js'
import './shared/events.js'

/**
 * Wait for legacy scripts to populate window.ALL with data.
 * @param {number} timeout - Max wait in ms
 * @returns {Promise<void>}
 */
/* [FIX 10-jun-2026] El timeout de 15 s expiraba en cargas frías/equipos lentos
   (slim 3,2 MB + parse + init de 5.281 markers) y la capa nueva entera
   (map-focus, ranking, discovery, panel-sync) moría EN SILENCIO dejando la
   home en modo legacy. Visto en prod: el error saltaba en cada carga y ALL
   llegaba DESPUÉS. Ahora: 45 s de margen y, si aun así expira, NO rechazamos
   — resolvemos en cuanto ALL aparezca (listener pasivo) y solo avisamos. */
function waitForData(timeout = 45000) {
  return new Promise((resolve) => {
    if (window.ALL?.length) return resolve()
    const start = Date.now()
    let warned = false
    const check = setInterval(() => {
      if (window.ALL?.length) { clearInterval(check); resolve() }
      else if (!warned && Date.now() - start > timeout) {
        warned = true
        console.warn('[bootstrap] ALL aún no poblado tras ' + timeout + 'ms — sigo esperando en segundo plano (modo legacy mientras tanto)')
      }
    }, 250)
  })
}

/**
 * Main bootstrap function. Called once from main.js.
 */
export async function bootstrap() {
  console.log('[bootstrap] Starting GelatoMaps module system...')

  try {
    // 1. Wait for legacy data
    await waitForData()
    console.log('[bootstrap] Data loaded:', window.ALL.length, 'shops')

    // 2. Hydrate store from legacy globals (ONE TIME)
    hydrateFromLegacy()

    // 3. Mount core feature bridges
    try {
      const { init: initMapFocus } = await import('./features/map/map-focus.js')
      if (typeof initMapFocus === 'function') initMapFocus()
      console.log('[bootstrap] ✓ map-focus')
    } catch (e) { console.warn('[bootstrap] ✗ map-focus:', e.message) }

    try {
      const { init: initRanking } = await import('./features/ranking/ranking-sidebar.js')
      if (typeof initRanking === 'function') initRanking()
      console.log('[bootstrap] ✓ ranking')
    } catch (e) { console.warn('[bootstrap] ✗ ranking:', e.message) }

    try {
      const { init: initCarousel } = await import('./features/carousel/discovery.js')
      if (typeof initCarousel === 'function') initCarousel()
      console.log('[bootstrap] ✓ carousel')
    } catch (e) { console.warn('[bootstrap] ✗ carousel:', e.message) }

    try {
      const { init: initPanelSync } = await import('./features/shop-panel/panel-sync.js')
      if (typeof initPanelSync === 'function') initPanelSync()
      console.log('[bootstrap] ✓ panel-sync')
    } catch (e) { console.warn('[bootstrap] ✗ panel-sync:', e.message) }

    // 4. Lazy-load admin bridge (wraps openAdmin)
    try {
      await import('./features/admin/admin-loader.js')
      console.log('[bootstrap] ✓ admin-loader')
    } catch (e) { console.warn('[bootstrap] ✗ admin-loader:', e.message) }

    // 5. Report
    const shops = store.get('shops')
    console.log(`[bootstrap] GelatoMaps module system ready (${shops?.length || 0} shops in store)`)

  } catch (e) {
    console.error('[bootstrap] Failed:', e)
    // App still works via legacy scripts
  }
}
